import { type FC, useRef, useEffect, useCallback, useState } from 'react';
import { getSrcsetUrl, getSrcsetString } from 'dibs-interiors-photos/exports/srcSet';
import classnames from 'classnames';

import styles from './PhotoContainer.scss';

const DEFAULT_IMAGE_WIDTH = 800;
const IMG_SIZES = '(max-width: 568px) 100vw, 67vw';

type PhotoAttributeType = {
    name: string | null;
};

type Props = {
    shouldShowImage?: boolean;
    photo: Partial<{
        name: string | null;
        path: string | null;
        width: number | null;
        height: number | null;
        room: PhotoAttributeType | null;
        styles: ReadonlyArray<PhotoAttributeType | null> | null;
    }>;
    index?: number;
    forceOpacity?: boolean;
};

const PhotoContainer: FC<Props> = props => {
    const { shouldShowImage, photo, index = 0, forceOpacity } = props;

    const photoPath = photo?.path || '';
    const [imagePath, setImagePath] = useState(photoPath);
    const [isVisible, setIsVisible] = useState(false);

    const timeoutRef = useRef<NodeJS.Timeout>(undefined);

    const showImage = useCallback(() => {
        timeoutRef.current = setTimeout(() => setIsVisible(true), 30);
    }, []);

    const isFirstRunRef = useRef(true);
    useEffect(() => {
        if (shouldShowImage && isFirstRunRef.current) {
            showImage();
            isFirstRunRef.current = false;
        }
    }, [shouldShowImage, showImage]);

    const photoRoom = photo?.room?.name || '';
    const photoStyle = photo?.styles?.[0]?.name || '';
    const photoAltDataExist = photoRoom || photoStyle;
    const imageAlt = photoAltDataExist ? `${photoStyle} ${photoRoom}` : photo?.name;

    useEffect(() => {
        setImagePath(photoPath);
        return () => {
            setIsVisible(false);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [photoPath]);

    if (shouldShowImage) {
        return (
            <img
                onLoad={showImage}
                className={classnames(styles.photo, {
                    [styles.fadeIn]: isVisible,
                    [styles.forceOpacity]: forceOpacity,
                })}
                src={getSrcsetUrl(imagePath, photo?.width || DEFAULT_IMAGE_WIDTH)}
                srcSet={getSrcsetString(imagePath)}
                sizes={IMG_SIZES}
                alt={imageAlt || ''}
                data-tn={`interiors-photo-${index}`}
            />
        );
    }

    return null;
};

export default PhotoContainer;
